import { useState } from "react";
import { FileSpreadsheet, Loader2, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Layout";
import { api, formatError } from "@/lib/api";

const TONE = { ready: "text-emerald-700 dark:text-emerald-400", fixed: "text-emerald-700 dark:text-emerald-400", no_change: "text-slate-500", error: "text-red-600" };
const LABEL = { ready: "Ready", fixed: "Fixed", no_change: "Skipped", error: "Needs fixing" };

const Results = ({ r }) => (
  <div className="overflow-x-auto mt-4 rounded-lg border border-slate-200 dark:border-slate-800 max-h-80">
    <table className="w-full text-xs" data-testid="fix-report-results">
      <thead><tr className="text-left uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
        {["Row", "Letter", "Result", "Details"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
      <tbody>{r.rows.map((x) => (
        <tr key={x.row} data-testid={`fix-report-row-${x.row}`} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
          <td className="px-3 py-2 font-mono">{x.row}</td><td className="px-3 py-2 font-mono">{x.reference}</td>
          <td className={`px-3 py-2 font-semibold ${TONE[x.result]}`}>{LABEL[x.result]}</td><td className="px-3 py-2">{x.message}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const FixFromReport = ({ batchId, onDone }) => {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (dryRun) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("csv_file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      const { data } = await api.post(`/bulk/batches/${batchId}/problems/fix`, fd);
      setRes(data);
      if (!dryRun && data.applied) { toast.success(`${data.applied} letter(s) updated and resent`); onDone(); }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = (e) => { setFile(e.target.files?.[0] || null); setRes(null); };
  return (
    <Panel title="Fix from report" testId="fix-from-report-panel">
      <p className="text-sm text-slate-600 dark:text-slate-300">Download the undelivered letters report, correct only the email or mobile columns, then upload it here. We check every row first, and nothing changes until you confirm. Only letters already in this batch can be updated.</p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800">
          <FileSpreadsheet size={15} /> {file ? file.name : "Choose corrected CSV"}
          <input data-testid="fix-report-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={pick} /></label>
        <button data-testid="fix-report-check-btn" onClick={() => send(true)} disabled={!file || busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Check file</button>
        {res?.dry_run && !res.errors && res.ready > 0 && (
          <button data-testid="fix-report-apply-btn" onClick={() => send(false)} disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-50">
            <Send size={14} /> Fix and resend {res.ready} letter{res.ready === 1 ? "" : "s"}</button>
        )}
      </div>
      {res && (
        <p data-testid="fix-report-summary" className="text-sm mt-3">
          {res.dry_run ? `${res.ready} ready · ${res.no_change} unchanged · ${res.errors} need fixing` : `${res.applied} updated and resent`}
          {res.dry_run && res.errors > 0 && <span className="text-red-600"> · Fix the highlighted rows and check again. Nothing has been changed.</span>}
        </p>
      )}
      {res && <Results r={res} />}
    </Panel>
  );
};
